import { EVENT_TYPE, ORDER, SORT } from "~/constants";
import useRequest from "~/hooks/use-request";
import Event from "./events/event.model";
import {
  formatCategorySearchRequestOptions,
  formatIdentity,
  formatIdentityItem,
  formatIdentitySubFacetsForMain,
  formatPaginatedResponseItems,
  formatRequestOptions,
  formatSearchRequestOptions,
  formatTeam,
  formatTeamWithLeagueSearch,
  returnPaginatedResponse,
} from "./format";
import HeaderAPI from "./header";

const SearchAPI = {
  eventSearch(options: IModelSearchOptions): Promise<IPaginatedResponse<IModelEvent>> {
    const { request } = useRequest();
    const params = formatSearchRequestOptions(options);

    if (options.type === EVENT_TYPE.HIGHLIGHT) {
      return request
        .post<IPaginatedResponse<IResponseEvent>>("/api/clip/list", { body: params })
        .then(response => Event.formatPaginated(response, Event.formatMediaEvent));
    }

    return request
      .post<IPaginatedResponse<IResponseEvent>>("/api/event/search", { body: params })
      .then(response => Event.formatPaginated(response, Event.format));
  },

  /**
   * @deprecated use `identitySubFacetsForMain` instead
   */
  async categoryWithSubcategorySearch(options: IModelCategorySearchOptions): Promise<IModelIdentityWithIdentityItemsSearch[]> {
    const { request } = useRequest();
    const appConfig = useRuntimeConfig().public;
    const cachedCategory = useSessionStorage<IHeaderLink[]>(`${appConfig.APP_CUSTOMER.name_short}-links`, []);

    if (!(cachedCategory.value && cachedCategory.value.length))
      await HeaderAPI.load(); // Check if category is already exists on session storage

    const params = formatCategorySearchRequestOptions(options);

    return request
      .post<IPaginatedResponse<IResponseCategorySearch>>("/api/identity/search", { body: params })
      .then((response) => {
        const categoryResults = response.content.entries.reduce((acc: IModelIdentityWithIdentityItemsSearch[], cur) => {
          const identityId = cur.mainIdentityId;
          const title = cachedCategory.value.find(c => c.id === identityId)?.title;
          if (!title)
            return acc;
          const item = { identityId, title, identityItems: cur.identityItems.map(formatIdentityItem) };
          return [...acc, item];
        }, []);

        return categoryResults;
      });
  },

  /**
   * @deprecated
   */
  categoryWithSubcategorySearchById(options: IModelCategorySearchOptionsWithIdentity): Promise<any> {
    return SearchAPI.categoryWithSubcategorySearch(options).then((response) => {
      if (response.length === 0)
        return Promise.resolve(returnPaginatedResponse([]));

      return returnPaginatedResponse(response[0].identityItems);
    });
  },

  /**
   * @deprecated use `identitySearch` instead
   */
  categorySearch(options: IRequestPaginatedOptions & { searchString?: string }): Promise<IPaginatedResponse<IModelIdentity>> {
    const { request } = useRequest();
    const generalParams = formatRequestOptions(options);
    const params = { ...generalParams, type: "identity", archived: options?.archived, searchString: options?.searchString };

    return request
      .post<IPaginatedResponse<IResponseIdentity>>("/api/identity/category_search", { body: params })
      .then(response => formatPaginatedResponseItems(response, formatIdentity));
  },

  identitySubFacetsForMain(options: IRequestCategorySearchWithSubcategoryOptions): Promise<IModelIdentitySubFacetsForMain[]> {
    const { request } = useRequest();

    const defaultOptions = {
      fields: "id,title,content",
      subsPerMainLimit: 10,
    };

    const params = Object.assign({}, defaultOptions, options);

    return request
      .get<IContentArrayResponse<IResponseIdentities>>("/api/identity/sub_facets_for_main", { query: params })
      .then(response => response.content.map(formatIdentitySubFacetsForMain));
  },

  identityItemSearch(
    options: IRequestPaginatedOptions & { searchString: string; identityId: string },
  ): Promise<IPaginatedResponse<IModelIdentityItem>> {
    const { request } = useRequest();
    const generalParams = formatRequestOptions(options);

    const defaultOptions = {
      ...generalParams,
      type: "identity_item",
      searchString: options.searchString,
      identityId: options.identityId,
    };

    return request.post<IPaginatedResponse<IResponseIdentity>>("/api/identity/advanced_search", { body: defaultOptions })
      .then(response => formatPaginatedResponseItems(response, formatIdentityItem));
  },

  identitySearch(
    options: IRequestPaginatedOptions & { searchString: string },
  ) {
    const { request } = useRequest();
    const generalParams = formatRequestOptions(options);

    const defaultOptions = {
      ...generalParams,
      type: "identity",
      searchString: options.searchString,
      archived: options?.archived,
    };

    return request.post<IPaginatedResponse<IResponseIdentity>>("/api/identity/advanced_search", { body: defaultOptions })
      .then(response => formatPaginatedResponseItems(response, formatIdentity));
  },

  teamSearch(options: IRequestPaginatedOptions & { title?: string; identityId?: string }) {
    const { request } = useRequest();
    const generalParams = formatRequestOptions(options);

    const defaultOptions = {
      ...generalParams,
      type: "team",
      searchString: options.title,
      identityId: options.identityId,
    };

    return request
      .get<IPaginatedResponse<IResponseTeam>>("/api/team/search", { query: defaultOptions })
      .then(response => formatPaginatedResponseItems(response, formatTeam));
  },

  leagueWithTeamsSearch(options: IRequestSearchLeagueWithTeamsOptions) {
    const { request } = useRequest();
    const defaultParams = {
      sortIdentity: SORT.order,
      orderIdentity: ORDER.asc,
      sortTeam: SORT.title,
      orderTeam: ORDER.asc,
    };

    const defaultOptions = Object.assign({}, defaultParams, options);

    return request
      .post<IContentResponse<IResponseLeagueWithTeamsSearch[]>>("/api/follow/team_list", { body: defaultOptions })
      .then(response => response.content.map(formatTeamWithLeagueSearch));
  },
};

export default SearchAPI;
